import {ThreadPrimitive, useAui, useAuiState} from '@assistant-ui/react';
import {
  Banner,
  Button,
  CalloutIcon,
  DotProgress,
  Icon,
} from '@wandb/components';
import React, {type FC, useLayoutEffect, useMemo, useState} from 'react';

import {useActiveThreadLoadState} from '../../agentChat/store/context';
import {useNavContext} from '../NavContextProvider';
import {WBAgentComposer} from './WBAgentComposer';
import {AssistantMessage, UserMessage} from './WBAgentMessage';

export const WBAgentThread = () => {
  const {
    activeThreadId,
    isLoading,
    error: loadError,
  } = useActiveThreadLoadState();

  const aui = useAui();
  const {params} = useNavContext();
  const showOpenProjectBanner = !params.entityName || !params.projectName;

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <DotProgress aria-label="Loading thread" />
      </div>
    );
  }

  if (loadError && activeThreadId) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-12 px-16">
        <CalloutIcon icon="warning" color="red" size="md" />
        <span className="font-semibold text-base">
          Failed to load conversation
        </span>
        <Button
          variant="secondary"
          startIcon="reload-refresh"
          onPress={() => aui.threads().switchToThread(activeThreadId)}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <ThreadPrimitive.Root
      className="flex flex-1 min-h-0 flex-col"
      style={{
        ['--thread-max-width' as string]: '44rem',
      }}>
      <ThreadPrimitive.ViewportProvider>
        {showOpenProjectBanner ? (
          <Banner size="sm" isDismissible={false} ml={7} mr={8} mt={5} mb={4}>
            <span>
              <strong>Open a project</strong> to use the W&B agent.
            </span>
          </Banner>
        ) : null}
        <ThreadPrimitive.Viewport
          turnAnchor="top"
          autoScroll
          className="flex flex-1 flex-col overflow-y-auto scroll-smooth px-24 pt-12 [scrollbar-gutter:stable] [scrollbar-width:thin] [scrollbar-color:#0d0f1252_transparent]">
          <ThreadPrimitive.Empty>
            <div className="flex flex-1 justify-center items-center gap-8">
              <Icon
                aria-label="workspace-agent-logo"
                icon="lightboard-solid"
                color="teal"
              />
              <span className="text-center text-[20px] font-semibold">
                {' '}
                How can I help you today?
              </span>
            </div>
          </ThreadPrimitive.Empty>
          <div className="flex flex-col shrink-0">
            <DeferredMessages />
          </div>
        </ThreadPrimitive.Viewport>
        <div className="px-24 py-16">
          <WBAgentComposer />
        </div>
      </ThreadPrimitive.ViewportProvider>
    </ThreadPrimitive.Root>
  );
};

/**
 * DeferredMessages renders messages in the chat thread after an initial delay.
 * This works around a bug in `assistant-ui` [1] where switching threads after
 * remounting this component (e.g. when closing and reopening) can cause a crash
 * if the original thread had messages in it.
 *
 * [1] https://github.com/assistant-ui/assistant-ui/issues/3652
 */
const DeferredMessages: FC = () => {
  const components = useMemo(() => ({UserMessage, AssistantMessage}), []);

  // Use `useLayoutEffect` so both renders happen before paint; this prevents
  // any flickering.
  const [ready, setReady] = useState(false);
  useLayoutEffect(() => setReady(true), []);

  const messagesLength = useAuiState(s => s.thread.messages.length);
  if (!ready || messagesLength === 0) {
    return null;
  }

  return (
    <>
      {Array.from({length: messagesLength}, (_, index) => (
        <ThreadPrimitive.MessageByIndex
          key={index}
          index={index}
          components={components}
        />
      ))}
    </>
  );
};
